// =============================================================================
// ACT ENGLISH TEST BANK INDEX
// Unified index for all English test forms
// =============================================================================

// Import test forms
import * as form25MC1 from './testBank-english-25MC1.js';
import { formJ08, scoringTableJ08 } from './testBank-english-J08.js';
import { form25MC4, scoringTable25MC4 } from './testBank-english-25MC4.js';

// =============================================================================
// SUBJECT METADATA
// =============================================================================
export const subjectInfo = {
  id: 'english',
  name: 'English',
  icon: '📝',
  color: 'blue',
  totalTime: 45, // Standard time in minutes
  description: 'Grammar, punctuation, sentence structure, and rhetorical skills',
  hasPracticeBank: false
};

// =============================================================================
// CATEGORY DEFINITIONS
// =============================================================================
export const categories = {
  POW: { 
    id: 'POW',
    name: 'Production of Writing', 
    description: 'Topic development, organization, unity, and cohesion',
    skills: ['Organization', 'Unity', 'Cohesion', 'Topic Development']
  },
  KLA: { 
    id: 'KLA',
    name: 'Knowledge of Language', 
    description: 'Precise word choice, style, and tone consistency',
    skills: ['Word Choice', 'Style', 'Tone']
  },
  CSE: { 
    id: 'CSE',
    name: 'Conventions of Standard English', 
    description: 'Sentence structure, punctuation, and usage',
    skills: ['Grammar', 'Punctuation', 'Sentence Structure', 'Usage']
  }
};

// =============================================================================
// TEST FORMS DATA
// =============================================================================
export const testFormsData = {
  'english-25MC1': {
    id: 'english-25MC1',
    formCode: '25MC1',
    subject: 'english',
    year: 2025,
    source: 'Official ACT Prep Guide 2025-2026',
    totalQuestions: form25MC1.totalQuestions,
    scoredQuestions: form25MC1.scoredQuestions,
    fieldTestPositions: form25MC1.fieldTestPositions,
    timeLimit: form25MC1.timeLimit,
    questions: form25MC1.questions,
    passages: form25MC1.passages,
    scoringTable: form25MC1.scoringTable
  },
  'english-25MC4': {
    id: 'english-25MC4',
    formCode: '25MC4',
    subject: 'english',
    year: 2025,
    source: 'Official ACT Prep Guide 2025-2026 (Practice Test 4)',
    totalQuestions: form25MC4.totalQuestions,
    scoredQuestions: form25MC4.scoredQuestions,
    fieldTestPositions: form25MC4.fieldTestPositions,
    timeLimit: form25MC4.timeLimit,
    questions: form25MC4.questions,
    passages: form25MC4.passages,
    scoringTable: scoringTable25MC4
  },
  'english-J08': {
    id: 'english-J08',
    formCode: 'J08',
    subject: 'english',
    year: 2025,
    month: 'October',
    source: 'ACT Form J08 (October 2025)',
    totalQuestions: 40,
    scoredQuestions: 40,
    fieldTestPositions: [], // Field test questions already removed
    timeLimit: 28, // Modified time for scored questions only
    standardTime: 45,
    questions: formJ08.questions,
    passages: formJ08.passages,
    scoringTable: scoringTableJ08
  }
};

// =============================================================================
// TEST FORMS LIST (for UI display)
// =============================================================================
export const testFormsList = [
  {
    id: 'english-25MC1',
    formCode: '25MC1',
    name: 'Form 25MC1',
    description: 'Official ACT Prep Guide 2025-2026',
    year: 2025,
    totalQuestions: form25MC1.totalQuestions,
    scoredQuestions: form25MC1.scoredQuestions,
    hasFieldTest: true,
    timeLimit: form25MC1.timeLimit
  },
  {
    id: 'english-25MC4',
    formCode: '25MC4',
    name: 'Form 25MC4',
    description: 'Official ACT Prep Guide 2025-2026 (Practice Test 4)',
    year: 2025,
    totalQuestions: form25MC4.totalQuestions,
    scoredQuestions: form25MC4.scoredQuestions,
    hasFieldTest: true,
    timeLimit: form25MC4.timeLimit
  },
  {
    id: 'english-J08',
    formCode: 'J08',
    name: 'Form J08',
    description: 'October 2025 (Scored Questions Only)',
    year: 2025,
    month: 'October',
    totalQuestions: 40,
    scoredQuestions: 40,
    hasFieldTest: false,
    timeLimit: 28
  }
];

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get all questions for a specific test form
 */
export function getTestQuestions(formId) {
  const form = testFormsData[formId];
  if (!form) return [];
  return form.questions || [];
}

/**
 * Get only scored questions for a test form
 */
export function getScoredQuestions(formId) {
  const questions = getTestQuestions(formId);
  return questions.filter(q => q.isScored !== false);
}

/**
 * Calculate scaled score from raw score
 */
export function calculateTestScore(formId, rawScore) {
  const form = testFormsData[formId];
  if (!form || !form.scoringTable) return null;
  
  // Handle range-based scoring tables
  const table = form.scoringTable;
  if (table[rawScore] !== undefined) {
    return table[rawScore];
  }
  
  // Find closest score
  const scores = Object.keys(table).map(Number).sort((a, b) => b - a);
  for (const score of scores) {
    if (rawScore >= score) {
      return table[score];
    }
  }
  return 1;
}

/**
 * Check if form has real (non-placeholder) questions
 */
export function hasRealQuestions(formId) {
  const questions = getTestQuestions(formId);
  return questions.length > 0 && questions.some(q => q.question && q.choices);
}

/**
 * Get metadata for a specific form
 */
export function getFormMetadata(formId) {
  return testFormsData[formId] || null;
}

/**
 * Get passages for a form
 */
export function getPassages(formId) {
  const form = testFormsData[formId];
  return form?.passages || [];
}

/**
 * Get category name by ID
 */
export function getCategoryName(categoryId) {
  return categories[categoryId]?.name || categoryId;
}

// =============================================================================
// STATISTICS
// =============================================================================
export const stats = {
  totalForms: Object.keys(testFormsData).length,
  totalQuestions: Object.values(testFormsData).reduce((sum, form) => sum + form.totalQuestions, 0),
  totalScoredQuestions: Object.values(testFormsData).reduce((sum, form) => sum + form.scoredQuestions, 0),
  forms: testFormsList.map(f => ({
    id: f.id,
    name: f.name,
    questions: f.totalQuestions,
    scored: f.scoredQuestions
  }))
};

console.log(`[English] Loaded ${stats.totalForms} forms with ${stats.totalScoredQuestions} total scored questions`);

export default {
  subjectInfo,
  categories,
  testFormsData,
  testFormsList,
  getTestQuestions,
  getScoredQuestions,
  calculateTestScore,
  hasRealQuestions,
  getFormMetadata,
  getPassages,
  getCategoryName,
  stats
};
